import React, { useMemo, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { CrosshairIcon } from 'lucide-react';
import { usePower } from '../../contexts/PowerContext';
import { EVIDENCE_LABEL, POWER_SOURCE_LABEL, POWER_STATE_LABEL, powerLocationById, powerLocations } from '../../data/power';
import { confirmedLabel, freshnessOf, sourcesFor } from '../../utils/power';
import { CONFIDENCE_LABEL } from '../../utils/power';
import { PowerStatusPill, stateMarkerColour } from './PowerPrimitives';
import type { PowerFreshness, PowerState } from '../../types/power';
type StatusFilter = 'all' | PowerState;
type SourceFilter = 'all' | 'grid' | 'generator' | 'solar' | 'inverter';
type FreshnessFilter = 'all' | 'fresh' | 'recent';
const LEGEND: {
  state: PowerState;
  label: string;
}[] = [{
  state: 'on',
  label: 'ON'
}, {
  state: 'off',
  label: 'OFF'
}, {
  state: 'unstable',
  label: 'Uncertain'
}, {
  state: 'backup',
  label: 'Backup'
}, {
  state: 'unknown',
  label: 'Unknown'
}];

/**
 * The live power map.
 *
 * Markers are labelled price-pill style rather than bare coloured dots: the state
 * is written on the marker, so it survives greyscale and colour blindness. Only
 * location entities are plotted — never a reporter's position, and never the
 * coordinates of somebody's tenancy.
 */
export function PowerMap({
  selectedId,
  onSelect,
  height = 'h-[26rem]'
}: {
  selectedId: string | null;
  onSelect: (id: string) => void;
  height?: string;
}) {
  const {
    getLocationStatus,
    campusLocationId
  } = usePower();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [source, setSource] = useState<SourceFilter>('all');
  const [freshness, setFreshness] = useState<FreshnessFilter>('all');
  const campus = powerLocationById(campusLocationId);
  const plotted = useMemo(() => powerLocations.filter(location => !location.personal).map(location => ({
    location,
    status: getLocationStatus(location.id)
  })).filter(entry => status === 'all' ? true : entry.status.state === status).filter(entry => source === 'all' ? true : entry.status.source === source).filter(entry => {
    if (freshness === 'all') return true;
    const level: PowerFreshness = freshnessOf(entry.status.lastConfirmedAt);
    return freshness === 'fresh' ? level === 'fresh' : level === 'fresh' || level === 'recent';
  }), [freshness, getLocationStatus, source, status]);
  return <section aria-labelledby="power-map-heading" className="overflow-hidden rounded-2xl border border-line bg-surface shadow-panel" data-id="element-4166" data-mp="5xxsnlgrpwk0">
      <div className="flex flex-wrap items-center gap-2 p-5 pb-3" data-id="element-4167" data-mp="fnzleqk2g359">
        <h2 id="power-map-heading" className="mr-auto text-h4 font-semibold text-ink" data-id="element-4168" data-mp="fpaoeo5ot6vb">
          Live power map
        </h2>
        <MapSelect label="Status" value={status} onChange={value => setStatus(value as StatusFilter)} options={[{
        value: 'all',
        label: 'All statuses'
      }, ...LEGEND.map(item => ({
        value: item.state,
        label: item.label
      }))]} data-id="element-4169" data-mp="3jc16ohkaqkpu" />
        <MapSelect label="Source" value={source} onChange={value => setSource(value as SourceFilter)} options={[{
        value: 'all',
        label: 'All sources'
      }, {
        value: 'grid',
        label: 'Grid'
      }, {
        value: 'generator',
        label: 'Generator'
      }, {
        value: 'solar',
        label: 'Solar'
      }, {
        value: 'inverter',
        label: 'Inverter'
      }]} data-id="element-4170" data-mp="1rfyt4sp93lqg" />
        <MapSelect label="Freshness" value={freshness} onChange={value => setFreshness(value as FreshnessFilter)} options={[{
        value: 'all',
        label: 'Any freshness'
      }, {
        value: 'fresh',
        label: 'Confirmed under 15 min'
      }, {
        value: 'recent',
        label: 'Confirmed under 30 min'
      }]} data-id="element-4171" data-mp="18qsat6sdcw56" />
      </div>

      <div className={`relative ${height} w-full`} data-id="element-4172" data-mp="2twk73wjn77ye">
        {campus && <MapContainer center={[campus.lat, campus.lng]} zoom={15} scrollWheelZoom={false} attributionControl={false} className="h-full w-full" data-id="element-4173" data-mp="2yuls4n0mphci">
            <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" data-id="element-4174" data-mp="2p37wiqbst1on" />
            <RecentreOnSelection selectedId={selectedId} data-id="element-4175" data-mp="2b3q666iutgd6" />
            {plotted.map(({
          location,
          status: locationStatus
        }) => <Marker key={location.id} position={[location.lat, location.lng]} icon={markerIcon(locationStatus.state, location.id === selectedId)} keyboard title={`${location.name} — power ${POWER_STATE_LABEL[locationStatus.state]}`} alt={`${location.name}, power ${POWER_STATE_LABEL[locationStatus.state]}`} eventHandlers={{
          click: () => onSelect(location.id)
        }} data-id="element-4176" data-mp="2g55m5k66cwop" />)}
          </MapContainer>}

        {/* Selected location card, matching the panel language of the page. */}
        {selectedId && <div className="pointer-events-auto absolute bottom-3 left-3 right-3 z-[500] sm:right-auto sm:w-[19rem]" data-id="element-4177" data-mp="1ymnl54q9zvgg">
            <MapLocationCard locationId={selectedId} onReport={() => onSelect(selectedId)} data-id="element-4178" data-mp="1tue125budpm3" />
          </div>}

        <button type="button" onClick={() => onSelect(campusLocationId)} className="absolute bottom-3 right-3 z-[500] hidden h-11 w-11 items-center justify-center rounded-xl border border-line bg-surface text-ink-muted shadow-panel transition-colors duration-150 ease-out hover:text-ink sm:flex" data-id="element-4179" data-mp="27l9430zntchr">
          <CrosshairIcon className="h-4 w-4" aria-hidden data-id="element-4180" data-mp="n6z2uqnyvklz" />
          <span className="sr-only" data-id="element-4181" data-mp="n7iiu1u23yxd">Centre on campus</span>
        </button>
      </div>

      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-line px-5 py-3" data-id="element-4182" data-mp="i979rtbl5sg7">
        {LEGEND.map(item => <li key={item.state} className="flex items-center gap-1.5 text-caption text-ink-muted" data-id="element-4183" data-mp="2ic82hr70ry80">
            <span className="h-2.5 w-2.5 rounded-full" style={{
          background: stateMarkerColour(item.state)
        }} aria-hidden data-id="element-4184" data-mp="2nbcgqw8swm57" />
            {item.label}
          </li>)}
        <li className="ml-auto text-caption text-ink-subtle" data-id="element-4185" data-mp="3199alkvveeg6">
          {plotted.length} of {powerLocations.filter(item => !item.personal).length} locations
          shown
        </li>
      </ul>
    </section>;
}
function MapSelect({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: {
    value: string;
    label: string;
  }[];
}) {
  return <label className="inline-flex min-h-touch items-center rounded-full border border-line bg-surface px-1" data-id="element-4186" data-mp="3k0a8s6p625yo">
      <span className="sr-only" data-id="element-4187" data-mp="3ozn83qx855i5">{label}</span>
      <select value={value} onChange={event => onChange(event.target.value)} className="min-h-touch rounded-full bg-transparent px-3 text-body-sm text-ink focus:outline-none" data-id="element-4188" data-mp="3owhdxx9qndfk">
        {options.map(option => <option key={option.value} value={option.value} data-id="element-4189" data-mp="3p3kwb3cwe1th">
            {option.label}
          </option>)}
      </select>
    </label>;
}

/** Marker as a labelled pill, so state is never carried by colour alone. */
function markerIcon(state: PowerState, active: boolean): L.DivIcon {
  const colour = stateMarkerColour(state);
  return L.divIcon({
    className: '',
    html: `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:999px;background:${colour};color:#fff;font:600 11px/1.2 system-ui,sans-serif;box-shadow:0 1px 4px rgba(15,23,42,.35);${active ? 'outline:2px solid #fff;outline-offset:2px;' : ''}">${POWER_STATE_LABEL[state]}</span>`,
    iconSize: [54, 22],
    iconAnchor: [27, 11]
  });
}
function RecentreOnSelection({
  selectedId
}: {
  selectedId: string | null;
}) {
  const map = useMap();
  React.useEffect(() => {
    if (!selectedId) return;
    const location = powerLocationById(selectedId);
    if (location) map.panTo([location.lat, location.lng], {
      animate: true
    });
  }, [map, selectedId]);
  return null;
}

/** The card a marker opens: the answer, its confidence, its age and its evidence. */
export function MapLocationCard({
  locationId,
  onReport
}: {
  locationId: string;
  onReport: () => void;
}) {
  const {
    getLocationStatus,
    getReliability,
    reports
  } = usePower();
  const location = powerLocationById(locationId);
  if (!location) return null;
  const status = getLocationStatus(locationId);
  const reliability = getReliability(locationId);
  const sources = sourcesFor(locationId, reports);
  return <div className="rounded-xl border border-line bg-surface p-4 shadow-panel" data-id="element-4190" data-mp="go2myjxjjhqn">
      <div className="flex items-start justify-between gap-2" data-id="element-4191" data-mp="lnfbra7z2yvv">
        <p className="text-body font-semibold text-ink" data-id="element-4192" data-mp="loyt745d7fz8">{location.name}</p>
        <PowerStatusPill state={status.state} data-id="element-4193" data-mp="2zu537nnsrd0c" />
      </div>
      <dl className="mt-2 space-y-1 text-caption text-ink-muted" data-id="element-4194" data-mp="2uwlq4r2u569z">
        <div className="flex justify-between gap-3" data-id="element-4195" data-mp="2uyg0xcke7i0c">
          <dt data-id="element-4196" data-mp="38wefoj2mlggd">Confidence</dt>
          <dd className="text-ink" data-id="element-4197" data-mp="38vl494sq22rw">{CONFIDENCE_LABEL[status.confidence]}</dd>
        </div>
        <div className="flex justify-between gap-3" data-id="element-4198" data-mp="34vugl9yhs2rl">
          <dt data-id="element-4199" data-mp="2h4p2p0c43kjx">Last confirmed</dt>
          <dd className="cz-num text-ink" data-id="element-4200" data-mp="2h603qse6ae7c">{confirmedLabel(status.lastConfirmedAt)}</dd>
        </div>
        <div className="flex justify-between gap-3" data-id="element-4201" data-mp="2c6wyb6ua65yu">
          <dt data-id="element-4202" data-mp="2px8sdbafeesm">Reports in window</dt>
          <dd className="cz-num text-ink" data-id="element-4203" data-mp="2pxinfdabvc56">{status.reportCount}</dd>
        </div>
        <div className="flex justify-between gap-3" data-id="element-4204" data-mp="2l6sc7jf80f2k">
          <dt data-id="element-4205" data-mp="2l590850lkq6l">Source</dt>
          <dd className="text-ink" data-id="element-4206" data-mp="1xgy41f5ujeon">
            {sources.length === 0 ? 'Not stated' : sources.map(item => POWER_SOURCE_LABEL[item]).join(' + ')}
          </dd>
        </div>
        {status.evidence && <div className="flex justify-between gap-3" data-id="element-4207" data-mp="1xqw78woyx787">
            <dt data-id="element-4208" data-mp="1xo9xqriiohnc">Newest evidence</dt>
            <dd className="text-ink" data-id="element-4209" data-mp="1srb6cgq8mosu">{EVIDENCE_LABEL[status.evidence]}</dd>
          </div>}
        <div className="flex justify-between gap-3" data-id="element-4210" data-mp="1op6ftqv368af">
          <dt data-id="element-4211" data-mp="1os2czcz40sxt">Today&apos;s reliability</dt>
          <dd className="cz-num text-ink" data-id="element-4212" data-mp="1jro3rrlrvcy7">
            {reliability.sufficient ? `${Math.round((reliability.uptimeShare ?? 0) * 100)}%` : 'Not enough data'}
          </dd>
        </div>
      </dl>
      {status.conflicting && <p className="mt-2 rounded-lg border border-warn-100 bg-warn-50 px-2.5 py-1.5 text-caption text-warn-800" data-id="element-4213" data-mp="19yyvf5liqf73">
          Recent reports disagree here, so confidence is lower than usual.
        </p>}
      <button type="button" onClick={onReport} className="mt-3 inline-flex min-h-touch w-full items-center justify-center rounded-lg border border-line bg-canvas px-3 text-body-sm font-medium text-ink transition-colors duration-150 ease-out hover:border-line-strong" data-id="element-4214" data-mp="10yk7dyh66lzw">
        Report current status
      </button>
    </div>;
}